import React, { useState, useEffect } from 'react';
import adminService from '../services/adminService';
import { formatDateSafe, formatDisplayDate, extractDayFromDate } from '../utils/dateUtils';
import {
    TrendingUp,
    Users,
    DollarSign,
    Target,
    AlertCircle,
    Award,
    Activity,
    ChevronDown,
    ChevronUp,
    Package,
    ChevronRight,
    ArrowUp,
    ArrowDown,
    BarChart2,
    Layers,
    X,
    Calendar,
    User,
    CreditCard
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, ReferenceLine } from 'recharts';
import ClusterCustomerModal from '../components/ClusterCustomerModal';
import ProfitabilityTrendChart from '../components/ProfitabilityTrendChart';
import CategoryPieChart from '../components/CategoryPieChart';
import SalesTrendChart from '../components/SalesTrendChart';
import CategoryTrendChart from '../components/CategoryTrendChart';
import CategoryProfitabilityChart from '../components/CategoryProfitabilityChart';
import InventoryValueChart from '../components/InventoryValueChart';
import InventoryTurnoverChart from '../components/InventoryTurnoverChart';
import FinancialEquityCard from '../components/dashboard/FinancialEquityCard';
import DailyCashFlowSection from '../components/dashboard/DailyCashFlowSection'; // IMPORT
import { financialService } from '../services/api'; // IMPORT financialService

const ExecutiveDashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [advancedStats, setAdvancedStats] = useState(null);
    const [shippingStats, setShippingStats] = useState(null);
    const [showShippingDetails, setShowShippingDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [expandedCities, setExpandedCities] = useState([]);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // Default to current month YYYY-MM
    const [inventoryValue, setInventoryValue] = useState(0);
    const [clusterModal, setClusterModal] = useState({ isOpen: false, cluster: null, customers: [], loading: false });
    const [productSortCriteria, setProductSortCriteria] = useState({}); // { categoryName: 'sales' | 'margin' | 'participation' }

    // Cash Flow State
    const [dailyCashFlow, setDailyCashFlow] = useState([]);
    const [cashFlowKPIs, setCashFlowKPIs] = useState({});
    const [loadingCashFlow, setLoadingCashFlow] = useState(false);

    // Sorting State
    const [sortConfig, setSortConfig] = useState({
        customer: { key: 'total_profit', direction: 'desc' },
        product: { key: 'total_profit', direction: 'desc' },
        city: { key: 'total_profit', direction: 'desc' }
    });

    const handleSort = (table, key) => {
        setSortConfig(prev => ({
            ...prev,
            [table]: {
                key,
                direction: prev[table].key === key && prev[table].direction === 'desc' ? 'asc' : 'desc'
            }
        }));
    };

    const getSortedData = (data, table) => {
        if (!data) return [];
        const { key, direction } = sortConfig[table];
        return [...data].sort((a, b) => { // Create a copy to avoid mutating original
            const aValue = parseFloat(a[key] || 0);
            const bValue = parseFloat(b[key] || 0);
            if (direction === 'asc') return aValue - bValue;
            return bValue - aValue;
        });
    };

    const [selectedDateRange, setSelectedDateRange] = useState({ start: '', end: '' });
    // Income Modal
    const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);
    const [incomeDetails, setIncomeDetails] = useState([]);
    const [loadingIncome, setLoadingIncome] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Calculate start and end dates based on selectedMonth
                const [year, month] = selectedMonth.split('-');
                const startDate = `${year}-${month}-01`;

                // Determine end date: use current date if in current month, otherwise use last day of selected month
                const now = new Date();
                const selectedMonthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
                const isCurrentMonth = now.getFullYear() === selectedMonthDate.getFullYear() &&
                    now.getMonth() === selectedMonthDate.getMonth();

                let endDate;
                if (isCurrentMonth) {
                    // Current month: use today's date
                    endDate = now.toISOString().slice(0, 10) + ' 23:59:59';
                } else {
                    // Past or future month: use last day of that month
                    const lastDayOfMonth = new Date(parseInt(year), parseInt(month), 0);
                    endDate = lastDayOfMonth.toISOString().slice(0, 10) + ' 23:59:59';
                }


                // Update state so Modal can use it
                setSelectedDateRange({ start: startDate, end: endDate });

                console.log(`📅 Fetching data for: ${startDate} to ${endDate} `);

                const [basicData, advancedData] = await Promise.all([
                    adminService.getExecutiveStats({ startDate, endDate }),
                    adminService.getAdvancedStats({ startDate, endDate })
                ]);

                if (basicData.success) {
                    console.log('💰 BASIC DATA KPIs:', basicData.kpis);
                    console.log('🔢 currentNetSales:', basicData.kpis?.currentNetSales);
                    console.log('🔢 totalOPEX:', basicData.kpis?.totalOPEX);
                    console.log('🔢 currentGrossProfit:', basicData.kpis?.currentGrossProfit);
                    setStats(basicData);
                }
                if (advancedData.success) {
                    console.log('🔍 Advanced Stats Data:', advancedData);
                    console.log('📊 Profit by Customer:', advancedData.data?.profitByCustomer);
                    console.log('📦 Profit by Product:', advancedData.data?.profitByProduct);
                    console.log('🏙️ Profit by City:', advancedData.data?.profitByCity);
                    setAdvancedStats(advancedData);
                    setAdvancedStats(advancedData);
                }

                // Fetch Shipping Stats separate to not block main load if it fails, but here we do parallel for simplicity
                const shippingData = await adminService.getShippingStats({ startDate, endDate });
                if (shippingData.success) {
                    setShippingStats(shippingData.data);
                }

                // Fetch Inventory Value History for Card
                const inventoryRes = await adminService.getInventoryValueHistory();
                if (inventoryRes.success && inventoryRes.data.length > 0) {
                    setInventoryValue(inventoryRes.data[inventoryRes.data.length - 1].value);
                }

                // Fetch Daily Cash Flow
                setLoadingCashFlow(true);
                try {
                    const cashFlowRes = await financialService.getDailyCashFlow({ startDate, endDate });
                    if (cashFlowRes.success) {
                        setDailyCashFlow(cashFlowRes.chart || []);
                        setCashFlowKPIs(cashFlowRes.kpis || {});
                    }
                } catch (err) {
                    console.error("Error loading cash flow:", err);
                } finally {
                    setLoadingCashFlow(false);
                }

            } catch (error) {
                console.error("Error loading dashboard data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedMonth]); // Refetch when month changes

    const fetchClusterCustomers = async (clusterType) => {
        setClusterModal({ isOpen: true, cluster: clusterType, customers: [], loading: true });
        try {
            const [year, month] = selectedMonth.split('-');
            const startDate = `${year}-${month}-01`;
            const endDate = new Date(year, month, 0).toISOString().slice(0, 10) + ' 23:59:59';

            const response = await adminService.getClusterCustomers(clusterType, { startDate, endDate });
            setClusterModal({ isOpen: true, cluster: clusterType, customers: response.customers, loading: false });
        } catch (error) {
            console.error('Error fetching cluster customers:', error);
            setClusterModal({ isOpen: true, cluster: clusterType, customers: [], loading: false });
        }
    };


    const fetchIncomeDetails = async () => {
        setIsIncomeModalOpen(true);
        setLoadingIncome(true);
        try {
            const token = localStorage.getItem('token');
            // If REACT_APP_API_URL includes /api, don't append it again. Or just use relative path for simplicity.
            // Best approach: Use clean relative path without env var in production to let Nginx handle it.
            const res = await fetch(`/api/financial/siigo-income?startDate=${selectedDateRange.start}&endDate=${selectedDateRange.end}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setIncomeDetails(data.details || []);
            }
        } catch (error) {
            console.error('Error details:', error);
        } finally {
            setLoadingIncome(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Cargando tablero gerencial...</div>;
    if (!stats || !stats.kpis || !stats.targets) return <div className="p-8 text-center text-red-500">Error: Datos incompletos.</div>;

    // Data Integrity Alert
    const zeroCostAlert = stats.zero_cost_count > 0 ? (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm flex items-start">
            <AlertCircle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
                <h3 className="text-red-800 font-bold text-lg">⚠️ Alerta de Integridad de Datos</h3>
                <p className="text-red-700 mt-1">
                    Se detectaron <strong>{stats.zero_cost_count} productos</strong> con costo $0 en este periodo.
                    Esto infla artificialmente la rentabilidad.
                </p>
                <button
                    className="mt-2 text-sm text-red-600 font-semibold hover:text-red-800 underline"
                    onClick={() => alert('Por favor contacte a soporte para ejecutar el script de corrección de costos.')}
                >
                    Ver detalles (Contactar Soporte)
                </button>
            </div>
        </div>
    ) : null;

    const { kpis, targets, productMix = [], strategies = [] } = stats;

    console.log('🐞 DEBUG ProductMix:', productMix);
    console.log('🐞 DEBUG Stats:', stats);

    // Backend now sends the correct COGS value (fixed: service items use 0 cost, not selling price)
    const costOfGoodsSold = kpis.currentCostSold;

    console.log('💰 FINANCIAL DEBUG:', {
        currentNetSales: kpis.currentNetSales,
        currentCostSold_FROM_BACKEND: kpis.currentCostSold,
        currentGrossProfit: kpis.currentGrossProfit,
        verification_NetSales_minus_Profit: kpis.currentNetSales - kpis.currentGrossProfit,
        difference: Math.abs(kpis.currentCostSold - (kpis.currentNetSales - kpis.currentGrossProfit))
    });

    // Helper para formato moneda
    const formatCurrency = (val) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(val) || 0);
    const formatPercent = (val) => `${Number(val || 0).toFixed(1)}% `;

    const getMonthName = (dateStr) => {
        if (!dateStr) return '';
        const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        return monthNames[parseInt(dateStr.split('-')[1], 10) - 1];
    };

    // Calcular porcentajes de meta
    const salesProgress = targets.sales ? Math.min((kpis.projectedSales / targets.sales) * 100, 100) : 0;
    const profitProgress = targets.profit ? Math.min((kpis.estimatedGrossProfit / targets.profit) * 100, 100) : 0;

    const currentMonthName = getMonthName(selectedMonth).toUpperCase();

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            {zeroCostAlert}

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Dashboard Ejecutivo</h1>
                    <p className="text-gray-600">Visión general y estrategia</p>
                </div>
                <div>
                    <input
                        type="month"
                        className="bg-white border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                </div>
            </div>

            {/* 1. Top Level KPIs - Financial Health */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">

                {/* --- CARD 1: ESTADO DE CAJA (DETALLADO) --- */}
                <div
                    className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col cursor-pointer hover:shadow-md transition-all duration-300 group"
                    onClick={fetchIncomeDetails}
                >
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1 group-hover:text-emerald-600 transition-colors">ESTADO DE CAJA</p>
                            <h2 className="text-3xl font-black text-slate-800 group-hover:scale-[1.01] transition-transform origin-left">{formatCurrency(kpis.totalIncome)}</h2>
                            <div className="flex items-center gap-1 mt-1">
                                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                <p className="text-[10px] text-emerald-600 font-bold uppercase">Recaudado Mes</p>
                            </div>
                        </div>
                        <div className="p-2 bg-emerald-50 text-emerald-500 rounded-xl">
                            <Activity size={20} />
                        </div>
                    </div>

                    <div className="space-y-3 mb-6">
                        <div className="bg-rose-50 border border-rose-100 p-3 rounded-xl flex justify-between items-center group hover:bg-rose-100 transition-colors">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-rose-500 text-white rounded-lg">
                                    <ArrowDown size={14} />
                                </div>
                                <span className="text-[10px] font-black text-rose-700 uppercase">Total Salidas</span>
                            </div>
                            <span className="text-sm font-black text-rose-600">{formatCurrency(kpis.totalOutflows)}</span>
                        </div>

                        <div className={`p-3 rounded-xl flex justify-between items-center border ${(kpis.totalIncome - kpis.totalOutflows) >= 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'} `}>
                            <div className="flex items-center gap-2">
                                <div className={`p-1.5 text-white rounded-lg ${(kpis.totalIncome - kpis.totalOutflows) >= 0 ? 'bg-emerald-500' : 'bg-rose-500'} `}>
                                    <ArrowDown size={14} />
                                </div>
                                <span className={`text-[10px] font-black uppercase ${(kpis.totalIncome - kpis.totalOutflows) >= 0 ? 'text-emerald-700' : 'text-rose-700'} `}>Flujo Neto (Caja)</span>
                            </div>
                            <span className={`text-sm font-black ${(kpis.totalIncome - kpis.totalOutflows) >= 0 ? 'text-emerald-600' : 'text-rose-600'} `}>
                                {formatCurrency(kpis.totalIncome - kpis.totalOutflows)}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-slate-50">
                        <div>
                            <div className="flex justify-between text-[10px] font-black mb-1">
                                <span className="text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                    <Layers size={10} /> SOLVENCIA
                                </span>
                                <span className="text-rose-500">{(cashFlowKPIs.solvency || 0).toFixed(0)}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-rose-500 transition-all duration-1000" style={{ width: `${Math.min(cashFlowKPIs.solvency || 0, 100)}%` }}></div>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-black mb-1">
                                <span className="text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                    <Target size={10} /> EFECTIVIDAD
                                </span>
                                <span className="text-emerald-500">{(cashFlowKPIs.effectiveness || 0).toFixed(0)}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${Math.min(cashFlowKPIs.effectiveness || 0, 100)}%` }}></div>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-[10px] font-black mb-1">
                                <span className="text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                    <Award size={10} /> CAJA / UTILIDAD
                                </span>
                                <span className="text-indigo-500">
                                    {kpis.currentGrossProfit > 0 ? ((kpis.totalIncome / kpis.currentGrossProfit) * 100).toFixed(0) : 0}%
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-indigo-500 transition-all duration-1000" style={{ width: '80%' }}></div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                            Mix de Salidas
                            <span className="flex items-center gap-4 text-slate-300 ml-auto">
                                <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div> INV</span>
                                <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-rose-400"></div> OPEX</span>
                            </span>
                        </p>
                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex">
                            <div className="h-full bg-indigo-500" style={{ width: `${((kpis.totalOutflows - kpis.totalOPEX) / kpis.totalOutflows) * 100}%` }}></div>
                            <div className="h-full bg-rose-400" style={{ width: `${((kpis.totalOPEX) / kpis.totalOutflows) * 100}%` }}></div>
                        </div>
                        <div className="flex justify-between mt-2 text-[9px] font-black text-slate-400 italic">
                            <span>{(((kpis.totalOutflows - kpis.totalOPEX) / kpis.totalOutflows) * 100).toFixed(0)}% Inversión</span>
                            <span>{(((kpis.totalOPEX) / kpis.totalOutflows) * 100).toFixed(0)}% Opex</span>
                        </div>
                    </div>
                </div>

                {/* --- CARD 2: VENTA DEL MES --- */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                    <div>
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">VENTAS NETAS {getMonthName(selectedMonth).toUpperCase()}</p>
                                <h2 className="text-3xl font-black text-slate-800 tracking-tighter">{formatCurrency(kpis.currentMonthSalesNoVat)}</h2>
                                <p className="text-[10px] text-emerald-500 font-bold uppercase mt-1">Valor Sin IVA (Sync P&L)</p>
                            </div>
                            <div className="p-2 bg-indigo-50 text-indigo-500 rounded-xl">
                                <TrendingUp size={20} />
                            </div>
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium mb-auto">Total Ventas (Con IVA): {formatCurrency(kpis.currentMonthSales)}</p>
                    </div>

                    <div className="mt-8">
                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 transition-all duration-1000 shadow-[0_0_8px_rgba(99,102,241,0.5)]" style={{ width: '100%' }}></div>
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-4">Acumulado del mes hasta hoy</p>
                    </div>
                </div>

                {/* --- CARD 3: PROYECCIÓN CIERRE --- */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                    <div>
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">PROYECCIÓN CIERRE</p>
                                <h2 className="text-3xl font-black text-slate-800 tracking-tighter">{formatCurrency(kpis.projectedSales)}</h2>
                                <p className="text-[10px] text-blue-500 font-bold uppercase mt-1">Valor antes de IVA</p>
                            </div>
                            <div className="p-2 bg-blue-50 text-blue-500 rounded-xl">
                                <Target size={20} />
                            </div>
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium">Total Proyectado (Con IVA): {formatCurrency(kpis.projectedSales)}</p>
                    </div>

                    <div className="mt-8">
                        <div className="flex justify-between items-end mb-2">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Meta: {formatCurrency(targets.sales)}</p>
                            <span className="text-[10px] font-black text-blue-600">{salesProgress.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 transition-all duration-1000" style={{ width: `${salesProgress}%` }}></div>
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-4 leading-relaxed">
                            Basado en velocidad diaria de <br />
                            <span className="text-slate-800">{formatCurrency(kpis.dailySalesVelocity)}</span>
                        </p>
                    </div>
                </div>

                {/* --- CARD 4: RESULTADO REAL (P&L) (DETALLADO) --- */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">RESULTADO REAL (P&L)</p>
                            <h2 className="text-3xl font-black text-slate-800 tracking-tighter">{formatCurrency((kpis.currentGrossProfit || 0) - (kpis.totalOPEX || 0))}</h2>
                            <div className="flex items-center gap-1 mt-1">
                                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                <p className="text-[10px] text-emerald-600 font-bold uppercase">UTILIDAD NETA FINAL</p>
                            </div>
                        </div>
                        <div className="p-2 bg-emerald-50 text-emerald-500 rounded-xl">
                            <Activity size={20} />
                        </div>
                    </div>

                    <div className="space-y-3 mb-6 p-1">
                        <div className="flex justify-between items-center text-[10px] font-medium border-b border-slate-50 pb-2">
                            <span className="text-slate-600">Ventas Netas (Sin IVA)</span>
                            <span className="font-black text-slate-900">{formatCurrency(kpis.currentNetSales)}</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-medium border-b border-slate-50 pb-2">
                            <span className="text-rose-500">(-) Costo de Mercancía Vendida</span>
                            <span className="font-black text-rose-600">{formatCurrency(costOfGoodsSold)}</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-black bg-slate-50 px-2 py-2 rounded-lg">
                            <div className="flex items-center gap-2">
                                <div className="p-1 shadow-sm bg-white rounded border border-slate-200"><TrendingUp size={10} className="text-slate-400" /></div>
                                <span className="text-slate-800 uppercase">Utilidad Bruta</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[8px] bg-slate-200 px-1.5 py-0.5 rounded-full text-slate-600">{((kpis.currentGrossProfit / (kpis.currentNetSales || 1)) * 100).toFixed(1)}%</span>
                                <span className="text-slate-900">{formatCurrency(kpis.currentGrossProfit)}</span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-medium border-b border-slate-50 pb-2">
                            <span className="text-rose-500">(-) Gastos Operativos (OPEX)</span>
                            <span className="font-black text-rose-600">{formatCurrency(kpis.totalOPEX)}</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-black bg-emerald-50 p-2 rounded-lg border border-emerald-100">
                            <div className="flex items-center gap-2">
                                <div className="p-1 shadow-sm bg-emerald-500 text-white rounded"><TrendingUp size={10} /></div>
                                <span className="text-emerald-700 uppercase">Utilidad Neta Total</span>
                            </div>
                            <span className="text-emerald-800">{formatCurrency((kpis.currentGrossProfit || 0) - (kpis.totalOPEX || 0))}</span>
                        </div>
                    </div>

                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 mb-6">
                        <div className="flex items-start gap-2">
                            <AlertCircle size={14} className="text-amber-500 mt-0.5" />
                            <div>
                                <p className="text-[10px] font-black text-amber-800 uppercase tracking-tighter">Margen Neto: {(((kpis.currentGrossProfit - (kpis.totalOPEX || 0)) / (kpis.currentNetSales || 1)) * 100).toFixed(1)}%</p>
                                <p className="text-[8px] text-amber-600 mt-1 leading-tight italic">Margen ajustado. Revisa tus gastos operativos o aumenta el ticket promedio.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <div className="flex-1 bg-slate-50 p-2 rounded-lg border border-slate-100">
                            <p className="text-[7px] font-black text-rose-500 uppercase tracking-tighter">EST. IVA (GUARDAR)</p>
                            <p className="text-[11px] font-black text-slate-800">{formatCurrency((kpis.currentMonthSales || 0) - (kpis.currentNetSales || 0))}</p>
                        </div>
                        <div className="flex-1 bg-slate-50 p-2 rounded-lg border border-slate-100 text-right">
                            <p className="text-[7px] font-black text-indigo-500 uppercase tracking-tighter">VALOR ACTIVOS BODEGA</p>
                            <p className="text-[11px] font-black text-slate-800">{formatCurrency(inventoryValue)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECCIÓN: INDICADORES FINANCIEROS CLAVE */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg">
                        <TrendingUp size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-gray-800">Indicadores Financieros Clave</h2>
                        <p className="text-sm text-gray-500 font-medium">Métricas estratégicas de eficiencia y liquidez</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* 1. RENTABILIDAD OPERATIVA */}
                    {(() => {
                        const utilidadOperativa = (kpis.currentGrossProfit || 0) - (kpis.totalOPEX || 0);
                        const ventasNetas = kpis.currentNetSales || 0;

                        // Validate that we have meaningful data to calculate
                        const hasValidData = ventasNetas > 0;
                        const rentabilidadOperativa = hasValidData ? (utilidadOperativa / ventasNetas) * 100 : 0;
                        const isPositive = rentabilidadOperativa >= 0;

                        const healthStatus = !hasValidData
                            ? { label: 'SIN DATOS', color: 'bg-gray-400', textColor: 'text-gray-600', bgLight: 'bg-gray-50' }
                            : rentabilidadOperativa >= 20 ? { label: 'EXCELENTE', color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50' }
                                : rentabilidadOperativa >= 10 ? { label: 'BUENO', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50' }
                                    : rentabilidadOperativa >= 5 ? { label: 'ACEPTABLE', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50' }
                                        : rentabilidadOperativa >= 0 ? { label: 'BAJO', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50' }
                                            : { label: 'PÉRDIDA', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50' };

                        return (
                            <div className={`${healthStatus.bgLight} border-2 ${healthStatus.color.replace('bg-', 'border-')} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className={`p-2 ${healthStatus.color} text-white rounded-lg`}>
                                        <TrendingUp size={18} />
                                    </div>
                                    <h3 className="text-sm font-black text-gray-800 uppercase tracking-wide">Rentabilidad Operativa</h3>
                                </div>

                                <div className="flex items-baseline gap-2 mb-2">
                                    {hasValidData ? (
                                        <>
                                            <span className={`text-5xl font-black ${healthStatus.textColor}`}>
                                                {Math.abs(rentabilidadOperativa).toFixed(1)}
                                            </span>
                                            <span className={`text-2xl font-black ${healthStatus.textColor} opacity-70`}>%</span>
                                        </>
                                    ) : (
                                        <span className="text-4xl font-black text-gray-400">N/A</span>
                                    )}
                                </div>

                                <span className={`inline-block text-[9px] font-black px-2 py-1 rounded-full ${healthStatus.color} text-white mb-3`}>
                                    {healthStatus.label}
                                </span>

                                <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                    {!hasValidData ? 'No hay datos de ventas en este periodo' :
                                        isPositive ? 'Ganancia operativa por cada peso vendido' : 'Pérdida operativa - revisar costos y OPEX'}
                                </p>

                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>Utilidad Operativa</span>
                                        <span className={healthStatus.textColor}>{formatCurrency(utilidadOperativa)}</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>Ventas Netas</span>
                                        <span>{hasValidData ? formatCurrency(ventasNetas) : 'N/A'}</span>
                                    </div>
                                </div>

                                <div className="mt-4 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${healthStatus.color} transition-all duration-1000`}
                                        style={{ width: `${Math.min(Math.abs(rentabilidadOperativa) * 4, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* 2. CICLO DE CONVERSIÓN DE EFECTIVO */}
                    {(() => {
                        // CCC = DII + DSO - DPO
                        // DII (Days Inventory Held) = (Inventario Promedio / COGS) * 365
                        // DSO (Days Sales Outstanding) = (Cuentas por Cobrar / Ventas) * 365
                        // DPO (Days Payable Outstanding) = (Cuentas por Pagar / COGS) * 365

                        const inventarioActual = inventoryValue || 0;
                        const cogs = kpis.currentCostSold || 1;
                        const cuentasPorCobrar = kpis.financialDeltas?.receivables || 0;
                        const ventas = kpis.currentNetSales || 1;
                        const cuentasPorPagar = kpis.financialDeltas?.payables || 0;

                        // Calcular en base a datos del mes (aproximado)
                        const diasMes = 30;
                        const DII = (inventarioActual / cogs) * diasMes;
                        const DSO = (cuentasPorCobrar / ventas) * diasMes;
                        const DPO = (cuentasPorPagar / cogs) * diasMes;

                        const CCC = DII + DSO - DPO;

                        const healthStatus =
                            CCC <= 30 ? { label: 'EXCELENTE', color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50' } :
                                CCC <= 60 ? { label: 'BUENO', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50' } :
                                    CCC <= 90 ? { label: 'ACEPTABLE', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50' } :
                                        { label: 'CRÍTICO', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50' };

                        return (
                            <div className={`${healthStatus.bgLight} border-2 ${healthStatus.color.replace('bg-', 'border-')} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className={`p-2 ${healthStatus.color} text-white rounded-lg`}>
                                        <Activity size={18} />
                                    </div>
                                    <h3 className="text-sm font-black text-gray-800 uppercase tracking-wide">Ciclo Conversión Efectivo</h3>
                                </div>

                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className={`text-5xl font-black ${healthStatus.textColor}`}>
                                        {Math.abs(CCC).toFixed(0)}
                                    </span>
                                    <span className={`text-xl font-black ${healthStatus.textColor} opacity-70`}>días</span>
                                </div>

                                <span className={`inline-block text-[9px] font-black px-2 py-1 rounded-full ${healthStatus.color} text-white mb-3`}>
                                    {healthStatus.label}
                                </span>

                                <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                    Tiempo promedio para convertir inversión en efectivo disponible
                                </p>

                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>📦 Días en Inventario (DII)</span>
                                        <span className="text-orange-600">+{DII.toFixed(0)}d</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>💰 Días Cobranza (DSO)</span>
                                        <span className="text-blue-600">+{DSO.toFixed(0)}d</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>📋 Días Pago Prov. (DPO)</span>
                                        <span className="text-emerald-600">-{DPO.toFixed(0)}d</span>
                                    </div>
                                </div>

                                <div className="mt-4 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${healthStatus.color} transition-all duration-1000`}
                                        style={{ width: `${Math.min((120 - CCC) / 120 * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* 3. RIESGO DE INVENTARIO */}
                    {(() => {
                        // Riesgo = (Valor Inventario / Ventas Mes) * 100
                        // Indica qué % del inventario representa vs ventas del periodo
                        const valorInventario = inventoryValue || 0;
                        const ventasMes = kpis.currentNetSales || 1;
                        const riesgoInventario = (valorInventario / ventasMes) * 100;

                        const healthStatus =
                            riesgoInventario <= 30 ? { label: 'BAJO', color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50' } :
                                riesgoInventario <= 60 ? { label: 'MODERADO', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50' } :
                                    riesgoInventario <= 100 ? { label: 'ALTO', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50' } :
                                        { label: 'CRÍTICO', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50' };

                        // Rotación estimada (veces por año)
                        const rotacionAnual = ventasMes > 0 ? (ventasMes * 12) / valorInventario : 0;

                        return (
                            <div className={`${healthStatus.bgLight} border-2 ${healthStatus.color.replace('bg-', 'border-')} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className={`p-2 ${healthStatus.color} text-white rounded-lg`}>
                                        <Package size={18} />
                                    </div>
                                    <h3 className="text-sm font-black text-gray-800 uppercase tracking-wide">Riesgo de Inventario</h3>
                                </div>

                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className={`text-5xl font-black ${healthStatus.textColor}`}>
                                        {riesgoInventario.toFixed(0)}
                                    </span>
                                    <span className={`text-2xl font-black ${healthStatus.textColor} opacity-70`}>%</span>
                                </div>

                                <span className={`inline-block text-[9px] font-black px-2 py-1 rounded-full ${healthStatus.color} text-white mb-3`}>
                                    RIESGO {healthStatus.label}
                                </span>

                                <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                                    Proporción de capital inmovilizado en inventario vs ventas mensuales
                                </p>

                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>Valor Inventario</span>
                                        <span className={healthStatus.textColor}>{formatCurrency(valorInventario)}</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>Ventas Mes</span>
                                        <span>{formatCurrency(ventasMes)}</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500">
                                        <span>Rotación Anual</span>
                                        <span className="text-indigo-600">{rotacionAnual.toFixed(1)}x</span>
                                    </div>
                                </div>

                                <div className="mt-4 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${healthStatus.color} transition-all duration-1000`}
                                        style={{ width: `${Math.min(riesgoInventario, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>


            {/* 1.5. Puente de Liquidez Real (NUEVO DISEÑO PREMIUM) */}
            <div className="bg-[#0f172a] text-white p-6 rounded-2xl shadow-xl mb-8 border border-slate-800">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Activity className="text-emerald-400" size={24} />
                            <h2 className="text-xl font-black tracking-tight">Puente de Liquidez: ¿Dónde está el dinero?</h2>
                        </div>
                        <p className="text-slate-400 text-sm font-medium">Explicación técnica de la diferencia entre Ganancia y Caja real.</p>
                    </div>
                    {(() => {
                        const impactCartera = -(kpis.financialDeltas?.receivables || 0);
                        const impactInventario = -(kpis.financialDeltas?.inventory || 0);
                        const impactDeuda = (kpis.financialDeltas?.payables || 0);
                        const balanceFinal = (kpis.currentGrossProfit || 0) + impactCartera + impactInventario + impactDeuda;

                        return (
                            <div className="bg-rose-500/10 border border-rose-500/20 px-4 py-2 rounded-full">
                                <span className="text-rose-400 text-xs font-black uppercase tracking-wider mr-2">Flujo Neto Mes:</span>
                                <span className={`font-black ${balanceFinal >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {formatCurrency(balanceFinal)}
                                </span>
                            </div>
                        );
                    })()}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {(() => {
                        const impactCartera = -(kpis.financialDeltas?.receivables || 0);
                        const impactInventario = -(kpis.financialDeltas?.inventory || 0);
                        const impactDeuda = (kpis.financialDeltas?.payables || 0);
                        const balanceFinal = (kpis.currentGrossProfit || 0) + impactCartera + impactInventario + impactDeuda;

                        const items = [
                            {
                                id: 1,
                                title: '1. GANANCIA',
                                value: kpis.currentGrossProfit || 0,
                                subtitle: 'Utilidad real generada por la operación en este periodo.',
                                icon: <TrendingUp size={18} />,
                                color: 'text-emerald-400',
                                bg: 'bg-emerald-500/5',
                                border: 'border-emerald-500/20'
                            },
                            {
                                id: 2,
                                title: '2. CARTERA',
                                value: impactCartera,
                                subtitle: impactCartera >= 0
                                    ? '¡Excelente! Recuperaste dinero de la calle (cobro de facturas antiguas).'
                                    : 'Dinero atrapado en la calle (ventas a crédito no cobradas aún).',
                                icon: <Users size={18} />,
                                color: impactCartera >= 0 ? 'text-emerald-400' : 'text-rose-400',
                                bg: impactCartera >= 0 ? 'bg-emerald-500/5' : 'bg-rose-500/5',
                                border: impactCartera >= 0 ? 'border-emerald-500/20' : 'border-rose-500/20'
                            },
                            {
                                id: 3,
                                title: '3. INVENTARIO',
                                value: impactInventario,
                                subtitle: impactInventario >= 0
                                    ? 'Liberaste caja al vender stock existente sin reponer todo.'
                                    : 'Compraste más mercancía de la que vendiste (dinero atrapado en bodega).',
                                icon: <Package size={18} />,
                                color: impactInventario >= 0 ? 'text-emerald-400' : 'text-rose-400',
                                bg: impactInventario >= 0 ? 'bg-emerald-500/5' : 'bg-rose-500/5',
                                border: impactInventario >= 0 ? 'border-emerald-500/20' : 'border-rose-500/20'
                            },
                            {
                                id: 4,
                                title: '4. DEUDA (CxP)',
                                value: impactDeuda,
                                subtitle: impactDeuda >= 0
                                    ? 'Financiamiento con proveedores (dinero que conservas en caja).'
                                    : 'Pagaste deudas a proveedores, lo cual redujo tu caja disponible.',
                                icon: <DollarSign size={18} />,
                                color: impactDeuda >= 0 ? 'text-emerald-400' : 'text-rose-400',
                                bg: impactDeuda >= 0 ? 'bg-emerald-500/5' : 'bg-rose-500/5',
                                border: impactDeuda >= 0 ? 'border-emerald-500/20' : 'border-rose-500/20'
                            }
                        ];

                        return (
                            <>
                                {items.map(item => (
                                    <div key={item.id} className={`p-5 rounded-xl border ${item.border} ${item.bg} flex flex-col justify-between h-full transition-all hover:scale-[1.02]`}>
                                        <div>
                                            <div className={`flex items-center gap-2 mb-4 ${item.color} opacity-80 uppercase font-black text-[10px] tracking-widest`}>
                                                {item.icon}
                                                {item.title}
                                            </div>
                                            <div className={`text-2xl font-black mb-3 ${item.color}`}>
                                                {item.value > 0 ? '+ ' : item.value < 0 ? '- ' : ''}
                                                {formatCurrency(Math.abs(item.value))}
                                            </div>
                                        </div>
                                        <p className="text-slate-500 text-[10px] leading-relaxed font-medium italic">
                                            {item.subtitle}
                                        </p>
                                    </div>
                                ))}

                                {/* Balance Final Box */}
                                <div className="p-5 rounded-xl border border-indigo-500/30 bg-indigo-600/20 flex flex-col justify-between h-full shadow-lg shadow-indigo-500/10 transition-all hover:scale-[1.02]">
                                    <div>
                                        <div className="flex items-center gap-2 mb-4 text-indigo-300 opacity-80 uppercase font-black text-[10px] tracking-widest">
                                            <Activity size={18} />
                                            BALANCE FINAL
                                        </div>
                                        <div className="text-2xl font-black mb-3 text-white">
                                            {balanceFinal > 0 ? '+ ' : balanceFinal < 0 ? '- ' : ''}
                                            {formatCurrency(Math.abs(balanceFinal))}
                                        </div>
                                    </div>
                                    <p className="text-indigo-300/60 text-[10px] leading-relaxed font-black uppercase tracking-tighter">
                                        {balanceFinal >= 0 ? 'AUMENTO NETO DE DINERO (RESERVA)' : 'DISMINUCIÓN NETA DE DINERO (USO DE AHORROS)'}
                                    </p>
                                </div>
                            </>
                        );
                    })()}
                </div>

                <div className="mt-8 pt-4 border-t border-slate-800/50 flex justify-center gap-6 text-[9px] font-black text-slate-600 tracking-[0.2em] uppercase overflow-x-auto whitespace-nowrap scrollbar-hide">
                    <span>GANANCIA</span>
                    <span className="text-slate-800">›</span>
                    <span>CARTERA</span>
                    <span className="text-slate-800">›</span>
                    <span>INVENTARIO</span>
                    <span className="text-slate-800">›</span>
                    <span>DEUDA</span>
                    <span className="text-slate-800">›</span>
                    <span className="text-indigo-400">DINERO EN BANCO</span>
                </div>
            </div>

            {/* 1.6. Patrimonio Financiero (CARD SEPARADA) */}
            <div className="mb-8">
                <FinancialEquityCard />
            </div>

            {/* 1.5. Flujo de Caja (NUEVO) */}
            <DailyCashFlowSection
                data={dailyCashFlow}
                kpis={cashFlowKPIs}
                loading={loadingCashFlow}
            />

            {/* 1.6. Tendencia de Ventas */}
            <SalesTrendChart selectedMonth={selectedMonth} />

            {/* 1.6. Tendencia de Rentabilidad */}
            <ProfitabilityTrendChart selectedMonth={selectedMonth} />

            {/* 1.6. Inventario (Valor y Rotación) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 mb-8">
                <InventoryValueChart />
                <InventoryTurnoverChart />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-full mr-4">
                        <Users size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Clientes Activos (Mes)</p>
                        <p className="text-2xl font-bold text-gray-800">{kpis.activeCustomers}</p>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-orange-50 text-orange-600 rounded-full mr-4">
                        <Activity size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Tasa de Retención</p>
                        <p className="text-2xl font-bold text-gray-800">{formatPercent(kpis.retentionRate)}</p>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-teal-50 text-teal-600 rounded-full mr-4">
                        <Award size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Ticket Promedio (AOV)</p>
                        <p className="text-2xl font-bold text-gray-800">{formatCurrency(kpis.aov)}</p>
                    </div>
                </div>
            </div>

            {/* 3 & 4. Product Strategy & Distribution (Now Vertical Flow) */}
            <div className="flex flex-col gap-8 mb-8">
                {/* Cards Section */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Rentabilidad por Categoría</h3>
                            <p className="text-xs text-gray-500 mt-1">
                                <span className="font-semibold">Participación:</span> % del total de ventas •
                                <span className="font-semibold ml-2">Margen:</span> % de ganancia sobre venta
                            </p>
                        </div>
                        <span className="text-xs text-gray-400 font-medium bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                            💰 Ventas vs Utilidad
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {productMix?.map((cat, idx) => {
                            const totalSales = productMix?.reduce((acc, curr) => acc + Number(curr.sales_value), 0) || 0;
                            const percent = totalSales > 0 ? (Number(cat.sales_value) / totalSales) * 100 : 0;
                            const profit = Number(cat.total_profit || 0);
                            const margin = Number(cat.sales_value) > 0 ? (profit / Number(cat.sales_value)) * 100 : 0;
                            const isExpanded = expandedCategory === idx;

                            // Asignar colores por marca (Normalizado a Mayúsculas)
                            let colorClass = 'bg-gray-400';
                            let textColor = 'text-gray-600';
                            let bgColor = 'bg-gray-50';

                            const catName = String(cat.category_group || '').toUpperCase().trim();
                            if (catName.includes('LIQUIPOPS')) { colorClass = 'bg-pink-500'; textColor = 'text-pink-600'; bgColor = 'bg-pink-50'; }
                            if (catName.includes('GENIALITY')) { colorClass = 'bg-purple-500'; textColor = 'text-purple-600'; bgColor = 'bg-purple-50'; }
                            if (catName.includes('SKARCHA')) { colorClass = 'bg-orange-500'; textColor = 'text-orange-600'; bgColor = 'bg-orange-50'; }
                            if (catName.includes('YEXIS')) { colorClass = 'bg-yellow-500'; textColor = 'text-yellow-600'; bgColor = 'bg-yellow-50'; }
                            if (catName.includes('BUBOLS')) { colorClass = 'bg-red-500'; textColor = 'text-red-600'; bgColor = 'bg-red-50'; }
                            if (catName.includes('LIQUIMON') || catName.includes('BASE')) { colorClass = 'bg-lime-500'; textColor = 'text-lime-600'; bgColor = 'bg-lime-50'; }
                            if (catName.includes('POLVO') || catName.includes('MEZCLA')) { colorClass = 'bg-blue-600'; textColor = 'text-blue-600'; bgColor = 'bg-blue-50'; }
                            if (catName.includes('SIROPE')) { colorClass = 'bg-cyan-500'; textColor = 'text-cyan-600'; bgColor = 'bg-cyan-50'; }
                            if (catName.includes('SALSA')) { colorClass = 'bg-amber-600'; textColor = 'text-amber-600'; bgColor = 'bg-amber-50'; }
                            if (catName.includes('EMPAQUE')) { colorClass = 'bg-slate-500'; textColor = 'text-slate-600'; bgColor = 'bg-slate-50'; }

                            return (
                                <div
                                    key={idx}
                                    className={`group border ${isExpanded ? 'border-indigo-200 shadow-lg ring-2 ring-indigo-50' : 'border-gray-100'} rounded-xl overflow-hidden transition-all duration-200 h-fit`}
                                >
                                    <div
                                        className={`p-4 cursor-pointer transition-colors ${isExpanded ? 'bg-gradient-to-br from-gray-50 to-white' : 'hover:bg-gray-50/50'}`}
                                        onClick={() => setExpandedCategory(isExpanded ? null : idx)}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center space-x-2 flex-1">
                                                <div className={`w-1.5 h-7 rounded-full ${colorClass}`}></div>
                                                <h4 className="font-bold text-gray-800 text-sm tracking-tight leading-tight">{cat.category_group}</h4>
                                            </div>
                                            <span
                                                className={`text-[10px] font-black px-2 py-1 rounded-full ${bgColor} ${textColor} whitespace-nowrap ml-2`}
                                                title="Participación en ventas totales"
                                            >
                                                📊 {percent.toFixed(1)}%
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-3">
                                            <div>
                                                <p className="text-[9px] text-gray-400 uppercase font-bold mb-1 tracking-wide">💵 Ventas</p>
                                                <p className="text-base font-black text-gray-800">{formatCurrency(Number(cat.sales_value))}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[9px] text-gray-400 uppercase font-bold mb-1 tracking-wide">💰 Utilidad</p>
                                                <p className={`text-base font-black ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatCurrency(profit)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wide">Margen de beneficio</span>
                                            <span
                                                className={`text-xs font-black px-2 py-1 rounded-lg ${margin >= 25 ? 'bg-green-100 text-green-700' :
                                                        margin >= 15 ? 'bg-blue-100 text-blue-700' :
                                                            margin >= 5 ? 'bg-yellow-100 text-yellow-700' :
                                                                'bg-red-100 text-red-700'
                                                    }`}
                                                title="Porcentaje de ganancia sobre el valor de venta"
                                            >
                                                {margin.toFixed(1)}%
                                            </span>
                                        </div>

                                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${colorClass} transition-all duration-1000 ease-out`}
                                                style={{ width: `${percent}%` }}
                                            ></div>
                                        </div>

                                        <div className="mt-3 flex justify-center">
                                            {isExpanded ? (
                                                <div className="flex items-center gap-2 text-xs text-indigo-600 font-semibold">
                                                    <span>Ocultar productos</span>
                                                    <ChevronUp size={14} />
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-xs text-gray-400 group-hover:text-gray-600 font-semibold transition-colors">
                                                    <span>Ver productos</span>
                                                    <ChevronDown size={14} />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Dropdown de Productos */}
                                    {isExpanded && cat.products && (
                                        <div className="border-t-2 border-indigo-100 bg-gradient-to-br from-gray-50 to-white p-4 max-h-[500px] overflow-y-auto">
                                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 pb-3 border-b-2 border-gray-100 sticky top-0 bg-white/95 backdrop-blur-sm z-10 -mt-1 pt-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-wide">
                                                        📦 Detalle por producto
                                                    </span>
                                                    <span className="text-[9px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                        {cat.products.length} items
                                                    </span>
                                                </div>
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                    <span className="text-[9px] text-gray-500 font-semibold uppercase tracking-wide">Ordenar por:</span>
                                                    <div className="flex items-center gap-1 bg-white p-1 rounded-lg border-2 border-indigo-100 shadow-sm">
                                                        {[
                                                            { id: 'sales', label: '💵 Ventas', tooltip: 'Ordenar por volumen de ventas' },
                                                            { id: 'margin', label: '📈 Margen', tooltip: 'Ordenar por % de ganancia' },
                                                            { id: 'participation', label: '📊 Part.', tooltip: 'Ordenar por participación' }
                                                        ].map(criterion => (
                                                            <button
                                                                key={criterion.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setProductSortCriteria(prev => ({
                                                                        ...prev,
                                                                        [cat.category_group]: criterion.id
                                                                    }));
                                                                }}
                                                                title={criterion.tooltip}
                                                                className={`px-2.5 py-1 rounded-md text-[9px] font-bold transition-all ${(productSortCriteria[cat.category_group] || 'sales') === criterion.id
                                                                        ? 'bg-indigo-600 text-white shadow-md scale-105'
                                                                        : 'text-gray-500 hover:bg-indigo-50 hover:text-indigo-600'
                                                                    }`}
                                                            >
                                                                {criterion.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                {[...cat.products]
                                                    .sort((a, b) => {
                                                        const crit = productSortCriteria[cat.category_group] || 'sales';
                                                        if (crit === 'sales') return b.sales - a.sales;
                                                        if (crit === 'margin') {
                                                            const marginA = a.sales > 0 ? (a.profit / a.sales) * 100 : -Infinity;
                                                            const marginB = b.sales > 0 ? (b.profit / b.sales) * 100 : -Infinity;
                                                            return marginB - marginA;
                                                        }
                                                        if (crit === 'participation') return b.sales - a.sales;
                                                        return 0;
                                                    })
                                                    .map((prod, pIdx) => {
                                                        const prodPercent = (prod.sales / Number(cat.sales_value)) * 100;
                                                        const prodMargin = prod.sales > 0 ? (prod.profit / prod.sales) * 100 : 0;

                                                        return (
                                                            <div
                                                                key={pIdx}
                                                                className="bg-white p-3 rounded-lg border border-gray-200 hover:border-indigo-200 hover:shadow-md transition-all group/item"
                                                            >
                                                                <div className="flex justify-between items-center mb-2">
                                                                    <span
                                                                        className="text-sm font-bold text-gray-800 truncate flex-1 pr-3"
                                                                        title={prod.name}
                                                                    >
                                                                        {prod.name}
                                                                    </span>
                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                        <span
                                                                            className={`text-[9px] font-black px-2 py-1 rounded-md whitespace-nowrap ${prodMargin >= 25 ? 'bg-green-100 text-green-700 border border-green-200' :
                                                                                    prodMargin >= 15 ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                                                                                        prodMargin >= 5 ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                                                                                            'bg-red-100 text-red-700 border border-red-200'
                                                                                }`}
                                                                            title="Margen de beneficio"
                                                                        >
                                                                            📈 {prodMargin.toFixed(1)}%
                                                                        </span>
                                                                        <span
                                                                            className="text-[9px] font-black text-gray-600 bg-gray-100 px-2 py-1 rounded-md border border-gray-200 whitespace-nowrap"
                                                                            title="Participación en la categoría"
                                                                        >
                                                                            📊 {prodPercent.toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2 overflow-hidden">
                                                                    <div
                                                                        className={`h-full rounded-full ${colorClass} transition-all duration-700`}
                                                                        style={{ width: `${prodPercent}%` }}
                                                                    ></div>
                                                                </div>

                                                                <div className="flex justify-between items-center text-[10px]">
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-gray-500 font-semibold">Ventas:</span>
                                                                        <span className="text-gray-700 font-bold">{formatCurrency(prod.sales)}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-gray-500 font-semibold">Utilidad:</span>
                                                                        <span className={`font-black ${prod.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                            {formatCurrency(prod.profit)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pie Chart Section */}
                <div className="w-full h-[550px]">
                    <CategoryPieChart selectedMonth={selectedMonth} externalData={productMix} />
                </div>
            </div>

            {/* 7. Category Trends (Sales & Profitability) */}
            <div className="flex flex-col gap-8 mb-8">
                <CategoryTrendChart selectedMonth={selectedMonth} />
                <CategoryProfitabilityChart selectedMonth={selectedMonth} />
            </div>

            {/* Strategies moved below if needed, or removed */}
            {
                strategies && strategies.length > 0 && (
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
                        <div className="flex items-center mb-4">
                            <div className="p-2 bg-yellow-100 text-yellow-600 rounded-lg mr-3">
                                <TrendingUp size={20} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">Recomendaciones Estratégicas (IA)</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {strategies.map((strat, idx) => (
                                <div key={idx} className="flex items-start p-4 bg-gray-50 rounded-lg border-l-4 border-yellow-400">
                                    <AlertCircle className="text-yellow-500 mt-1 mr-3 flex-shrink-0" size={20} />
                                    <div>
                                        <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide mb-1">{strat.title}</h4>
                                        <p className="text-gray-600 text-xs">{strat.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }

            {/* 5. Advanced Growth Metrics */}
            {
                advancedStats && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Churn Risk */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center">
                                <AlertCircle size={20} className="mr-2" />
                                Riesgo de Fuga (Top 10)
                            </h3>
                            <div className="overflow-y-auto max-h-64">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Cliente</th>
                                            <th className="pb-2 text-right">Última Compra</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {advancedStats.data.churnRisk?.map((client, idx) => (
                                            <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">
                                                    <p className="font-medium text-gray-800">{client.name}</p>
                                                    <p className="text-xs text-gray-500">{client.phone}</p>
                                                </td>
                                                <td className="py-2 text-right text-gray-600">
                                                    {formatDisplayDate(client.last_purchase_date)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Cross-Sell Opportunity */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-blue-600 mb-4 flex items-center">
                                <Target size={20} className="mr-2" />
                                Oportunidad Cross-Sell
                            </h3>
                            <p className="text-xs text-gray-500 mb-3">Compran mucho (&gt;1M) pero NO Perlas</p>
                            <div className="overflow-y-auto max-h-64">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Cliente</th>
                                            <th className="pb-2 text-right">Ventas Periodo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {advancedStats.data.crossSell?.map((client, idx) => (
                                            <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">
                                                    <p className="font-medium text-gray-800">{client.name}</p>
                                                    <p className="text-xs text-gray-500">{client.phone}</p>
                                                </td>
                                                <td className="py-2 text-right font-bold text-gray-800">
                                                    {formatCurrency(Number(client.total_spent_period))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Top Cities */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-purple-600 flex items-center">
                                    <TrendingUp size={20} className="mr-2" />
                                    Top Ciudades ({currentMonthName})
                                </h3>
                                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                    Total: {formatCurrency(advancedStats.data.topCities?.reduce((acc, curr) => acc + Number(curr.total_sales), 0) || 0)}
                                </span>
                            </div>
                            <div className="space-y-4">
                                {advancedStats.data.topCities?.map((city, idx) => {
                                    const maxSales = Number(advancedStats.data.topCities[0].total_sales);
                                    const percent = (Number(city.total_sales) / maxSales) * 100;
                                    return (
                                        <div key={idx}>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="font-medium text-gray-700">{city.city}</span>
                                                <span className="font-bold text-gray-800">{formatCurrency(Number(city.total_sales))}</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2">
                                                <div
                                                    className="bg-purple-500 h-2 rounded-full"
                                                    style={{ width: `${percent}% ` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* 6. Strategic Financials & Inventory */}
            {
                advancedStats && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
                        {/* Cartera */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <DollarSign size={20} className="mr-2 text-green-600" />
                                Salud Financiera (Cartera)
                            </h3>
                            <div className="space-y-6">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Por Cobrar (Deuda Total)</p>
                                    <p className="text-3xl font-bold text-red-500">{formatCurrency(advancedStats.data.receivables?.totalDebt)}</p>
                                    <p className="text-xs text-gray-400">Pedidos entregados sin cerrar en Siigo</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Recaudado (Este Mes)</p>
                                    <p className="text-3xl font-bold text-green-600">{formatCurrency(advancedStats.data.receivables?.collectedMonth)}</p>
                                    <p className="text-xs text-gray-400">Pagos conciliados este mes</p>
                                </div>
                            </div>
                        </div>

                        {/* Mix de Clientes */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <Users size={20} className="mr-2 text-blue-600" />
                                Mix de Clientes (Mes)
                            </h3>
                            <div className="flex items-center justify-center h-48">
                                <div className="w-full space-y-4">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="font-medium text-blue-600">Nuevos ({advancedStats.data.customerMix?.newCustomers})</span>
                                            <span className="font-bold">{formatCurrency(advancedStats.data.customerMix?.newSales)}</span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-4">
                                            <div
                                                className="bg-blue-500 h-4 rounded-full"
                                                style={{ width: `${(advancedStats.data.customerMix?.newSales / (advancedStats.data.customerMix?.newSales + advancedStats.data.customerMix?.recurringSales)) * 100}% ` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="font-medium text-indigo-600">Recurrentes ({advancedStats.data.customerMix?.recurringCustomers})</span>
                                            <span className="font-bold">{formatCurrency(advancedStats.data.customerMix?.recurringSales)}</span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-4">
                                            <div
                                                className="bg-indigo-500 h-4 rounded-full"
                                                style={{ width: `${(advancedStats.data.customerMix?.recurringSales / (advancedStats.data.customerMix?.newSales + advancedStats.data.customerMix?.recurringSales)) * 100}% ` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inventory Runway */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <Activity size={20} className="mr-2 text-orange-600" />
                                Días de Inventario (Runway)
                            </h3>
                            <div className="overflow-y-auto max-h-64">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Producto</th>
                                            <th className="pb-2 text-right">Días</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {advancedStats.data.inventoryRunway?.map((item, idx) => {
                                            let daysClass = 'text-green-600';
                                            if (item.days_remaining < 15) daysClass = 'text-yellow-600';
                                            if (item.days_remaining < 7) daysClass = 'text-red-600 font-bold';

                                            return (
                                                <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                    <td className="py-2">
                                                        <p className="font-medium text-gray-800 truncate w-48" title={item.product_name}>{item.product_name}</p>
                                                        <p className="text-xs text-gray-500">Stock: {item.current_stock}</p>
                                                    </td>
                                                    <td className={`py-2 text-right ${daysClass} `}>
                                                        {item.days_remaining > 365 ? '> 1 año' : `${Math.round(item.days_remaining)} días`}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* 7. Rentabilidad Real por Segmento */}
            {
                advancedStats && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8 pb-8">
                        {/* Top Rentabilidad Clientes */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <DollarSign size={20} className="mr-2 text-green-600" />
                                Top Rentabilidad Clientes
                            </h3>
                            <div className="overflow-y-auto max-h-[600px]">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Cliente</th>
                                            <th
                                                className="pb-2 text-right cursor-pointer"
                                                onClick={() => handleSort('customer', 'total_profit')}
                                            >
                                                <div className={`flex items-center justify-end inline-flex px-2 py-1 rounded-lg transition-colors ${sortConfig.customer.key === 'total_profit' ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-100'} `}>
                                                    Utilidad
                                                    {sortConfig.customer.key === 'total_profit' && (
                                                        sortConfig.customer.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />
                                                    )}
                                                </div>
                                            </th>
                                            <th
                                                className="pb-2 text-right cursor-pointer"
                                                onClick={() => handleSort('customer', 'margin_percent')}
                                            >
                                                <div className={`flex items-center justify-end inline-flex px-2 py-1 rounded-lg transition-colors ${sortConfig.customer.key === 'margin_percent' ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-100'} `}>
                                                    %
                                                    {sortConfig.customer.key === 'margin_percent' && (
                                                        sortConfig.customer.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />
                                                    )}
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedData(advancedStats.data?.profitByCustomer, 'customer').map((item, idx) => (
                                            <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">
                                                    <p className="font-medium text-gray-800" title={item.name}>{item.name}</p>
                                                    <p className="text-xs text-gray-500">{formatCurrency(item.total_sales)}</p>
                                                </td>
                                                <td className="py-2 text-right font-bold text-green-600">
                                                    {formatCurrency(item.total_profit)}
                                                </td>
                                                <td className="py-2 text-right text-gray-600">
                                                    {formatPercent(item.margin_percent)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Top Rentabilidad Productos */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <Package size={20} className="mr-2 text-purple-600" />
                                Top Rentabilidad Productos
                            </h3>
                            <div className="overflow-y-auto max-h-[600px]">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Producto</th>
                                            <th
                                                className="pb-2 text-right cursor-pointer"
                                                onClick={() => handleSort('product', 'total_profit')}
                                            >
                                                <div className={`flex items-center justify-end inline-flex px-2 py-1 rounded-lg transition-colors ${sortConfig.product.key === 'total_profit' ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-100'} `}>
                                                    Utilidad
                                                    {sortConfig.product.key === 'total_profit' && (
                                                        sortConfig.product.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />
                                                    )}
                                                </div>
                                            </th>
                                            <th
                                                className="pb-2 text-right cursor-pointer"
                                                onClick={() => handleSort('product', 'margin_percent')}
                                            >
                                                <div className={`flex items-center justify-end inline-flex px-2 py-1 rounded-lg transition-colors ${sortConfig.product.key === 'margin_percent' ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-100'} `}>
                                                    %
                                                    {sortConfig.product.key === 'margin_percent' && (
                                                        sortConfig.product.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />
                                                    )}
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedData(advancedStats.data?.profitByProduct, 'product').map((item, idx) => (
                                            <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="py-2">
                                                    <p className="font-medium text-gray-800" title={item.product_name}>{item.product_name}</p>
                                                    <p className="text-xs text-gray-500">{formatCurrency(item.total_sales)}</p>
                                                </td>
                                                <td className="py-2 text-right font-bold text-purple-600">
                                                    {formatCurrency(item.total_profit)}
                                                </td>
                                                <td className="py-2 text-right text-gray-600">
                                                    {formatPercent(item.margin_percent)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Rentabilidad por Ciudad */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <TrendingUp size={20} className="mr-2 text-blue-600" />
                                Rentabilidad por Ciudad ({getMonthName(selectedMonth)})
                            </h3>
                            <div className="overflow-y-auto max-h-[600px]">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Ciudad</th>
                                            <th className="pb-2 text-right">Ventas</th>
                                            <th
                                                className="pb-2 text-right cursor-pointer"
                                                onClick={() => handleSort('city', 'total_profit')}
                                            >
                                                <div className={`flex items-center justify-end inline-flex px-2 py-1 rounded-lg transition-colors ${sortConfig.city.key === 'total_profit' ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-100'} `}>
                                                    Utilidad
                                                    {sortConfig.city.key === 'total_profit' && (
                                                        sortConfig.city.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />
                                                    )}
                                                </div>
                                            </th>
                                            <th
                                                className="pb-2 text-right cursor-pointer"
                                                onClick={() => handleSort('city', 'margin_percent')}
                                            >
                                                <div className={`flex items-center justify-end inline-flex px-2 py-1 rounded-lg transition-colors ${sortConfig.city.key === 'margin_percent' ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-100'} `}>
                                                    %
                                                    {sortConfig.city.key === 'margin_percent' && (
                                                        sortConfig.city.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />
                                                    )}
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedData(advancedStats.data?.profitByCity, 'city').map((item, idx) => (
                                            <React.Fragment key={idx}>
                                                <tr
                                                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                                                    onClick={() => {
                                                        const newExpanded = [...(expandedCities || [])];
                                                        const index = newExpanded.indexOf(idx);
                                                        if (index > -1) newExpanded.splice(index, 1);
                                                        else newExpanded.push(idx);
                                                        setExpandedCities(newExpanded);
                                                    }}
                                                >
                                                    <td className="py-2">
                                                        <div className="flex items-center">
                                                            {expandedCities?.includes(idx) ? <ChevronDown size={14} className="mr-1 text-gray-400" /> : <ChevronRight size={14} className="mr-1 text-gray-400" />}
                                                            <p className="font-medium text-gray-800">{item.city}</p>
                                                        </div>
                                                    </td>
                                                    <td className="py-2 text-right text-gray-600">
                                                        {formatCurrency(item.total_sales)}
                                                    </td>
                                                    <td className="py-2 text-right font-bold text-blue-600">
                                                        {formatCurrency(item.total_profit)}
                                                    </td>
                                                    <td className="py-2 text-right text-gray-600">
                                                        {formatPercent(item.margin_percent)}
                                                    </td>
                                                </tr>
                                                {expandedCities?.includes(idx) && item.top_customers && (
                                                    <tr className="bg-gray-50">
                                                        <td colSpan="3" className="p-0">
                                                            <div className="pl-6 pr-2 py-2 border-l-4 border-blue-100 my-1">
                                                                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Top 5 Clientes en {item.city}</p>
                                                                <table className="min-w-full text-xs">
                                                                    <thead>
                                                                        <tr className="text-gray-400 border-b border-gray-100">
                                                                            <th className="text-left font-normal pb-1">Cliente</th>
                                                                            <th className="text-right font-normal pb-1">Utilidad</th>
                                                                            <th className="text-right font-normal pb-1">%</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(() => {
                                                                            let customers = [];
                                                                            try {
                                                                                customers = typeof item.top_customers === 'string'
                                                                                    ? JSON.parse(item.top_customers)
                                                                                    : (item.top_customers || []);
                                                                                if (!Array.isArray(customers)) customers = [];
                                                                            } catch (e) {
                                                                                console.error('Error parsing top_customers:', e);
                                                                            }

                                                                            return customers.map((cust, custIdx) => (
                                                                                <tr key={custIdx} className="border-b border-gray-100 last:border-0">
                                                                                    <td className="py-1 truncate max-w-[140px]" title={cust.name}>{cust.name}</td>
                                                                                    <td className="py-1 text-right font-medium text-gray-700">{formatCurrency(cust.total_profit)}</td>
                                                                                    <td className="py-1 text-right text-gray-500">{formatPercent(cust.margin_percent)}</td>
                                                                                </tr>
                                                                            ));
                                                                        })()}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* 7. Rentabilidad Real por Segmento */}
            {
                advancedStats && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8 pb-8">
                        {/* ... (Existing Top Rentabilidad Clientes and Productos) ... omitted for brevity in previous context but kept here implicitly by structure */}

                        {/* Strategic Cluster Analysis - Full Width */}
                        <div className="lg:col-span-3">
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                                    <Layers size={24} className="mr-2 text-indigo-600" />
                                    Análisis Estratégico de Clientes (Clusters)
                                </h3>

                                {/* Cluster Summaries */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    {advancedStats.data.customerClusters?.map((cluster, idx) => {
                                        let theme = {
                                            bg: 'bg-slate-50',
                                            border: 'border-slate-100',
                                            accent: 'bg-emerald-500',
                                            text: 'text-slate-800',
                                            label: 'text-emerald-600',
                                            light: 'bg-emerald-50'
                                        };

                                        if (cluster.label.includes('titan') || cluster.label.includes('>20')) {
                                            theme = { bg: 'bg-indigo-50', border: 'border-indigo-100', accent: 'bg-indigo-600', text: 'text-indigo-900', label: 'text-indigo-600', light: 'bg-indigo-50' };
                                        } else if (cluster.label.includes('15-20')) {
                                            theme = { bg: 'bg-blue-50', border: 'border-blue-100', accent: 'bg-blue-600', text: 'text-blue-900', label: 'text-blue-600', light: 'bg-blue-50' };
                                        } else if (cluster.label.includes('5-14')) {
                                            theme = { bg: 'bg-emerald-50', border: 'border-emerald-100', accent: 'bg-emerald-600', text: 'text-emerald-900', label: 'text-emerald-600', light: 'bg-emerald-50' };
                                        } else {
                                            theme = { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'bg-slate-400', text: 'text-slate-800', label: 'text-slate-500', light: 'bg-slate-100' };
                                        }

                                        const profitMargin = cluster.totalSales > 0 ? ((cluster.totalProfit / cluster.totalSales) * 100).toFixed(1) : '0.0';

                                        return (
                                            <div
                                                key={idx}
                                                className={`p-5 rounded-2xl border ${theme.border} ${theme.bg} cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-300 relative overflow-hidden group`}
                                                onClick={() => fetchClusterCustomers(cluster.type)}
                                            >
                                                <div className={`absolute top-0 right-0 w-24 h-24 ${theme.accent} opacity-[0.03] rounded-bl-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500`}></div>

                                                <div className="flex justify-between items-start mb-4 relative z-10">
                                                    <div>
                                                        <p className={`text-[10px] font-black ${theme.label} uppercase tracking-widest mb-1`}>{cluster.label}</p>
                                                        <h4 className={`text-lg font-black ${theme.text} tracking-tight`}>{cluster.count} Clientes</h4>
                                                    </div>
                                                    <div className={`p-2 ${theme.light} ${theme.label} rounded-xl shadow-sm`}>
                                                        <Users size={18} />
                                                    </div>
                                                </div>

                                                <div className="space-y-3 relative z-10">
                                                    <div className="flex justify-between items-end">
                                                        <span className="text-[10px] font-black text-slate-400 uppercase">Ventas Totales</span>
                                                        <span className="text-sm font-black text-slate-800 tracking-tight">{formatCurrency(cluster.totalSales)}</span>
                                                    </div>

                                                    <div className="bg-white/60 backdrop-blur-sm p-3 rounded-xl border border-white/40 shadow-sm">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="text-[9px] font-black text-slate-400 uppercase">Rentabilidad Bruta</span>
                                                            <span className={`text-xs font-black ${cluster.totalProfit > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                                {formatCurrency(cluster.totalProfit)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden">
                                                                <div className={`h-full ${theme.accent} transition-all duration-1000`} style={{ width: `${Math.min(parseFloat(profitMargin) * 3, 100)}%` }}></div>
                                                            </div>
                                                            <span className="text-[10px] font-black text-slate-600">{profitMargin}%</span>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200/50">
                                                        <div>
                                                            <p className="text-[8px] font-black text-slate-400 uppercase">T. Promedio</p>
                                                            <p className="text-[11px] font-black text-slate-900">{formatCurrency(cluster.avgTicket)}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[8px] font-black text-slate-400 uppercase">Efic/Cliente</p>
                                                            <p className="text-[11px] font-black text-slate-900">{formatCurrency(cluster.efficiency)}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Scatter Chart */}
                                <div className="h-96 w-full">
                                    <h4 className="text-sm font-semibold text-gray-500 mb-4 text-center">Eficiencia Logística: Esfuerzo vs Recompensa</h4>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={advancedStats.data.customerClusters?.map(c => {
                                                const totalSystemOrders = advancedStats.data.customerClusters.reduce((acc, curr) => acc + (curr.totalOrders || 0), 0);
                                                const totalSystemProfit = advancedStats.data.customerClusters.reduce((acc, curr) => acc + (curr.totalProfit || 0), 0);

                                                let shortName = c.label.split(" ")[0];
                                                if (c.label.includes("Plata")) shortName = "Plata";
                                                if (c.label.includes("Oro")) shortName = "Oro";

                                                return {
                                                    name: shortName,
                                                    'Esfuerzo Logístico (% Pedidos)': totalSystemOrders > 0 ? (c.totalOrders / totalSystemOrders) * 100 : 0,
                                                    'Recompensa Financiera (% Ganancia)': totalSystemProfit > 0 ? (c.totalProfit / totalSystemProfit) * 100 : 0
                                                };
                                            })}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis unit="%" />
                                            <Tooltip formatter={(value) => `${value.toFixed(1)}% `} />
                                            <Legend />
                                            <Bar dataKey="Esfuerzo Logístico (% Pedidos)" fill="#EF4444" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="Recompensa Financiera (% Ganancia)" fill="#10B981" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                    <p className="text-center text-xs text-gray-500 mt-2 italic">
                                        "Si la barra Roja (Esfuerzo) es más alta que la Verde (Ganancia), ese grupo consume demasiados recursos."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* 8. Clientes con Baja Rentabilidad (Moved down) */}
            {
                advancedStats && advancedStats.data?.lowProfitCustomers?.length > 0 && (
                    <div className="mt-8 pb-8">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-red-200">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <AlertCircle size={20} className="mr-2 text-red-600" />
                                ⚠️ Clientes con Baja Rentabilidad (Margen &lt; 15%)
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">Estos clientes requieren atención: revisa precios, descuentos o costos de servicio</p>
                            <div className="overflow-y-auto max-h-96">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2">Cliente</th>
                                            <th className="pb-2 text-right">Ventas</th>
                                            <th className="pb-2 text-right">Utilidad</th>
                                            <th className="pb-2 text-right">Margen %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {advancedStats.data.lowProfitCustomers.map((item, idx) => (
                                            <tr key={idx} className="border-b last:border-0 hover:bg-red-50">
                                                <td className="py-2">
                                                    <p className="font-medium text-gray-800" title={item.name}>{item.name}</p>
                                                </td>
                                                <td className="py-2 text-right text-gray-600">
                                                    {formatCurrency(item.total_sales)}
                                                </td>
                                                <td className="py-2 text-right font-bold text-orange-600">
                                                    {formatCurrency(item.total_profit)}
                                                </td>
                                                <td className="py-2 text-right">
                                                    <span className={`font-bold ${Number(item.margin_percent) < 10 ? 'text-red-600' : 'text-orange-600'} `}>
                                                        {formatPercent(item.margin_percent)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* 9. Reporte Detallado de Fletes (Nuevo) */}
            {
                shippingStats && (
                    <div className="mt-8 pb-8">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center">
                                        <Package size={20} className="mr-2 text-indigo-600" />
                                        Auditoría de Fletes y Envíos
                                    </h3>
                                    <p className="text-sm text-gray-500">Desglose de cargos por fletes en facturas</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-800">{formatCurrency(shippingStats.summary.total_gross)}</p>
                                    <p className="text-xs text-gray-500">Total Fletes ({shippingStats.summary.count} pedidos)</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setShowShippingDetails(!showShippingDetails)}
                                className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors mb-4"
                            >
                                {showShippingDetails ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
                                {showShippingDetails ? 'Ocultar Detalle' : 'Ver Detalle de Facturas'}
                            </button>

                            {showShippingDetails && (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 text-left text-gray-500 border-b">
                                                <th className="py-3 px-4">Fecha</th>
                                                <th className="py-3 px-4">Pedido / Factura</th>
                                                <th className="py-3 px-4">Cliente</th>
                                                <th className="py-3 px-4">Ciudad</th>
                                                <th className="py-3 px-4 text-right">Valor Flete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {shippingStats.details.map((item, idx) => (
                                                <tr key={idx} className="border-b hover:bg-gray-50">
                                                    <td className="py-2 px-4 text-gray-600">{new Date(item.created_at).toLocaleDateString()}</td>
                                                    <td className="py-2 px-4 font-medium text-gray-800">
                                                        {item.order_number}
                                                        {item.siigo_invoice_number && <span className="ml-2 text-xs text-gray-500">({item.siigo_invoice_number})</span>}
                                                    </td>
                                                    <td className="py-2 px-4 text-gray-600">{item.customer_name}</td>
                                                    <td className="py-2 px-4 text-gray-600">{item.customer_city}</td>
                                                    <td className="py-2 px-4 text-right font-bold text-gray-800">{formatCurrency(item.total_charge)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }


            {/* Cluster Customer Modal */}
            <ClusterCustomerModal
                isOpen={clusterModal.isOpen}
                cluster={clusterModal.cluster}
                customers={clusterModal.customers}
                loading={clusterModal.loading}
                onClose={() => setClusterModal({ isOpen: false, cluster: null, customers: [], loading: false })}
                formatCurrency={formatCurrency}
                selectedMonth={selectedMonth}
            />
            {/* Income Detail Modal */}
            {
                isIncomeModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl transform transition-all flex flex-col max-h-[90vh]">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white rounded-t-xl">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                        <span className="text-orange-500">☰</span> Detalle de Ingresos Recaudados (Siigo)
                                    </h3>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Relación detallada de recibos para el periodo seleccionado
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsIncomeModalOpen(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X size={24} className="text-gray-400" />
                                </button>
                            </div>

                            <div className="p-0 overflow-auto flex-1 bg-gray-50">
                                {loadingIncome ? (
                                    <div className="flex justify-center py-12">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                                    </div>
                                ) : (
                                    <div className="min-w-full inline-block align-middle">
                                        <div className="bg-white m-6 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th scope="col" className="px-6 py-3 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">FECHA</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">RECIBO</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">CLIENTE</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">CUENTA / MEDIO</th>
                                                        <th scope="col" className="px-6 py-3 text-right text-[10px] font-black text-slate-400 uppercase tracking-wider">MONTO</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {incomeDetails.length > 0 ? (
                                                        incomeDetails.map((item, idx) => {
                                                            const isNewDate = idx === 0 || incomeDetails[idx - 1].date !== item.date;
                                                            return (
                                                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-medium">
                                                                        {isNewDate ? (
                                                                            <div className="flex items-center gap-2">
                                                                                <Calendar size={12} className="text-slate-300" />
                                                                                {formatDisplayDate(item.date)}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-slate-200 pl-5">"</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-indigo-600 font-bold">
                                                                        {item.receipt}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600">
                                                                        <div className="flex items-center gap-2">
                                                                            <User size={12} className="text-slate-300" />
                                                                            <div className="flex flex-col">
                                                                                <span className="font-bold text-slate-800" title={item.customer}>{item.customer}</span>
                                                                                <span className="text-[9px] text-slate-400">{item.identification}</span>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                                                                        <div className="flex items-center gap-2">
                                                                            <CreditCard size={12} className="text-slate-300" />
                                                                            {item.account || 'N/A'}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-right font-black text-slate-900 px-3 py-1">
                                                                        {formatCurrency(Number(item.amount))}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-400 bg-gray-50">
                                                                <div className="flex flex-col items-center justify-center">
                                                                    <AlertCircle size={48} className="mb-4 text-gray-300" />
                                                                    <p className="text-lg font-medium text-gray-500">No hay registros</p>
                                                                    <p className="text-sm text-gray-400">No se encontraron ingresos para el periodo {selectedDateRange.start} al {selectedDateRange.end}</p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Footer Summary moved inside modal body or keep separately */}
                                        {incomeDetails.length > 0 && (
                                            <div className="px-6 pb-6 flex justify-end gap-8 text-sm">
                                                <div className="text-right">
                                                    <span className="block text-gray-400 text-xs uppercase font-bold">Total Recibos</span>
                                                    <span className="block text-gray-800 font-bold text-lg">{incomeDetails.length}</span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="block text-gray-400 text-xs uppercase font-bold">Total Recaudado</span>
                                                    <span className="block text-gray-800 font-bold text-lg text-green-600">
                                                        {formatCurrency(incomeDetails.reduce((sum, item) => sum + Number(item.amount), 0))}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="p-4 border-t border-gray-100 bg-white rounded-b-xl flex justify-end">
                                <button
                                    onClick={() => setIsIncomeModalOpen(false)}
                                    className="px-6 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-800 font-medium shadow-sm transition-all"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default ExecutiveDashboardPage;
